if (!customElements.get('greeting-card-component')) {
  customElements.define('greeting-card-component', class GreetingCardComponent extends HTMLElement {
    constructor() {
      super();
      this.productFormId = this.dataset.productFormId;
      this.sectionId = this.dataset.sectionId;
      this.blockId = this.dataset.blockId;
      this.defaultCardType = this.dataset.defaultCardType || 'Paper';
      this.blockSettings = JSON.parse(this.dataset.blockSettings || '{}');
      
      // Get pre-formatted price data from data attributes
      this.paperCardPrice = this.dataset.paperCardPrice;
      this.ecardOriginalPrice = this.dataset.ecardOriginalPrice;
      this.ecardDiscountedPrice = this.dataset.ecardDiscountedPrice;

      // Bind methods
      this.boundUpdateFieldsVisibility = this.updateFieldsVisibility.bind(this);
      this.boundApplyDynamicLogic = this.applyDynamicInputLogic.bind(this);
    }

    connectedCallback() {
      if (!this.isConnected) return;

      this.cardTypeSelect = this.querySelector(`select[name="properties[Gift Card Type]"]`);
      this.titleContainer = this.querySelector(`#gift-message-title-${this.sectionId}-${this.blockId}`);
      this.messageContainer = this.querySelector(`#gift-message-text-${this.sectionId}-${this.blockId}`);
      this.modalButtonContainer = this.querySelector(`#gift-message-modal-button-${this.sectionId}-${this.blockId}`);
      this._greetingTypeInput = this.querySelector('input[name="properties[_Greeting Type]"]');
      this.priceDisplay = this.querySelector('.card-price-display');

      if (!this.cardTypeSelect || !this.titleContainer || !this.messageContainer) {
        console.warn('Greeting Card Component: Essential elements not found. Script will not run.');
        return;
      }

      this.titleInput = this.titleContainer.querySelector('input[name="properties[Title]"]');
      this.messageInput = this.messageContainer.querySelector('textarea[name="properties[Message]"], input[name="properties[Message]"]');

      // Note: The original script continued if titleInput or messageInput were not found.
      // We will proceed cautiously, checking for their existence before use.

      if (this.cardTypeSelect) {
        this.cardTypeSelect.value = this.defaultCardType;
        this.cardTypeSelect.addEventListener('change', this.boundUpdateFieldsVisibility);
      }

      if (this.titleInput) {
        this.titleInput.addEventListener('input', this.boundApplyDynamicLogic);
      }
      if (this.messageInput) {
        this.messageInput.addEventListener('input', this.boundApplyDynamicLogic);
      }

      this.updateFieldsVisibility(); // Initial setup
    }

    disconnectedCallback() {
      if (this.cardTypeSelect) {
        this.cardTypeSelect.removeEventListener('change', this.boundUpdateFieldsVisibility);
      }
      if (this.titleInput) {
        this.titleInput.removeEventListener('input', this.boundApplyDynamicLogic);
      }
      if (this.messageInput) {
        this.messageInput.removeEventListener('input', this.boundApplyDynamicLogic);
      }
    }

    updateFieldsVisibility() {
      const selectedValue = this.cardTypeSelect ? this.cardTypeSelect.value : this.defaultCardType;

      this.resetAllFieldsVisuals(); // Renamed to avoid confusion, only handles visuals and base attributes
      this.updatePriceDisplay(selectedValue); // Update the price display

      if (selectedValue === "Paper") {
        if (this.messageContainer) this.messageContainer.classList.remove('hidden');
      } else if (selectedValue === "Million eCard") {
        if (this.titleContainer) this.titleContainer.classList.remove('hidden');
        if (this.messageContainer) this.messageContainer.classList.remove('hidden');
        if (this.modalButtonContainer && this.blockSettings.modal_button && this.blockSettings.modal_text) {
          this.modalButtonContainer.classList.remove('hidden');
        }
      }
      // If 'None' or empty, all fields remain hidden by resetAllFieldsVisuals.

      this.applyDynamicInputLogic();
    }

    updatePriceDisplay(cardType) {
      if (!this.priceDisplay) return;
      
      if (cardType === "Million eCard") {
        // For eCard, check if we have price data to display
        if (!this.ecardOriginalPrice && !this.ecardDiscountedPrice) {
          this.priceDisplay.textContent = '';
          return;
        }
        
        // If we only have discounted price, show it alone
        if (this.ecardDiscountedPrice && !this.ecardOriginalPrice) {
          this.priceDisplay.innerHTML = this.ecardDiscountedPrice;
          return;
        }
        
        // If we only have original price, show it alone
        if (this.ecardOriginalPrice && !this.ecardDiscountedPrice) {
          this.priceDisplay.innerHTML = this.ecardOriginalPrice;
          return;
        }
        
        // If we have both prices and they're different, show discounted format
        if (this.ecardOriginalPrice !== this.ecardDiscountedPrice) {
          this.priceDisplay.innerHTML = `
            <span class="discounted-price">${this.ecardDiscountedPrice}</span>
            <span class="original-price">${this.ecardOriginalPrice}</span>
          `;
          
          // Add strikethrough styling to the original price
          const originalPrice = this.priceDisplay.querySelector('.original-price');
          if (originalPrice) {
            originalPrice.style.textDecoration = 'line-through';
            originalPrice.style.marginRight = '0.5em';
            originalPrice.style.color = 'var(--color-text-muted, #767676)';
          }
        } else {
          // If prices are the same, just show one
          this.priceDisplay.innerHTML = this.ecardDiscountedPrice;
        }
      } else {
        // For Paper Card
        if (!this.paperCardPrice) {
          this.priceDisplay.textContent = '';
          return;
        }
        
        this.priceDisplay.innerHTML = this.paperCardPrice;
      }
    }

    resetAllFieldsVisuals() {
      if (this.titleContainer) this.titleContainer.classList.add('hidden');
      if (this.titleInput) {
        this.titleInput.removeAttribute('required');
        this.titleInput.removeAttribute('form');
        // Value is intentionally not cleared here
      }

      if (this.messageContainer) this.messageContainer.classList.add('hidden');
      if (this.messageInput) {
        this.messageInput.removeAttribute('required');
        this.messageInput.removeAttribute('form');
        // Value is intentionally not cleared here
      }

      if (this.modalButtonContainer) {
        this.modalButtonContainer.classList.add('hidden');
      }

      // Reset the _Greeting Type input's form attribute
      if (this._greetingTypeInput) {
        this._greetingTypeInput.removeAttribute('form');
      }
    }

    applyDynamicInputLogic() {
      const visibleInputs = [];
      if (this.titleInput && this.titleContainer && !this.titleContainer.classList.contains('hidden')) {
        visibleInputs.push(this.titleInput);
      }
      if (this.messageInput && this.messageContainer && !this.messageContainer.classList.contains('hidden')) {
        visibleInputs.push(this.messageInput);
      }

      if (visibleInputs.length === 0) {
        // No visible inputs to process, ensure _Greeting Type is not added to the form
        if (this._greetingTypeInput) {
          this._greetingTypeInput.removeAttribute('form');
        }
        return;
      }

      // Update the greeting card type value based on the selected type
      if (this._greetingTypeInput && this.cardTypeSelect) {
        this._greetingTypeInput.value = this.cardTypeSelect.value || 'None';
      }

      let shouldAddToForm = false;

      if (visibleInputs.length === 1) {
        const singleInput = visibleInputs[0];
        singleInput.removeAttribute('required');
        if (singleInput.value.trim() !== '' && this.productFormId) {
          singleInput.setAttribute('form', this.productFormId);
          shouldAddToForm = true;
        } else {
          singleInput.removeAttribute('form');
        }
      } else { // More than one visible input
        const hasValueInAnyVisibleInput = visibleInputs.some(input => input.value.trim() !== '');
        visibleInputs.forEach(input => {
          if (hasValueInAnyVisibleInput) {
            input.setAttribute('required', 'required');
            if (this.productFormId) {
              input.setAttribute('form', this.productFormId);
              shouldAddToForm = true;
            }
          } else {
            input.removeAttribute('required');
            input.removeAttribute('form');
          }
        });
      }

      // Only include the _Greeting Type in the form if any other inputs are being added
      if (this._greetingTypeInput && this.productFormId) {
        if (shouldAddToForm) {
          this._greetingTypeInput.setAttribute('form', this.productFormId);
        } else {
          this._greetingTypeInput.removeAttribute('form');
        }
      }
    }

    // Removed showPaperCardFields and showECardFields methods
    // resetAllFields was renamed to resetAllFieldsVisuals and modified
  });
}
